import { CreateCodebookWarehouseRequest } from "@ns/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { FaTrashAlt } from "react-icons/fa";
import { IoCheckmark } from "react-icons/io5";
import { useParams } from "react-router-dom";

import { api } from "../api/custom-api.ts";
import { ControlledDropdown } from "../components/form/controlled-dropdown.tsx";
import ControlledForm from "../components/form/controlled-form.tsx";
import ControlledInput from "../components/form/controlled-input.tsx";

import { Button } from "../components/ui/button.tsx";

import Loader from "../components/loader.tsx";
import { toaster } from "../components/ui/toaster.tsx";

import {
  UpdateCodebookWarehouse,
  updateCodebookWarehouseSchema,
} from "../schemas/update-codebook-warehouse.schema.ts";
import { MaterialSelectProps } from "../utils/types/material-select.props.ts";
import { PlateSelectProps } from "../utils/types/plate-select.props.ts";
import ControlledTextArea from "../components/form/controlled-textarea.tsx";

const UpdateCodebookWarehouseForm = () => {
  const { setValue, reset, handleSubmit } =
    useFormContext<UpdateCodebookWarehouse>();

  const { id } = useParams();

  const { data, isLoading } = useQuery({
    queryKey: ["codebookWarehouse", id],
    queryFn: () => api.codebookWarehouse.getCodebookWarehouseById(id!),
    enabled: !!id,
    staleTime: 0,
    cacheTime: 0,
  });

  useEffect(() => {
    setValue("code", data?.code ?? "");
    setValue("name", data?.name ?? "");
    // @ts-ignore
    setValue("type", data?.type ? [data.type] : [""]);
    // @ts-ignore
    setValue("plateType", data?.plateType ? [data.plateType] : [""]);
    setValue("note", data?.note ?? "");
    setValue("stoneSurfaceTreatment", data?.stoneSurfaceTreatment ?? "");
  }, [data, setValue]);

  const resetFields = () => {
    reset({
      code: data?.code ?? "",
      name: data?.name ?? "",
      // @ts-ignore
      type: data?.type ? [data.type] : [""],
      // @ts-ignore
      plateType: data?.plateType ? [data.plateType] : [""],
      note: data?.note ?? "",
      stoneSurfaceTreatment: data?.stoneSurfaceTreatment ?? "",
    });
  };

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (request: CreateCodebookWarehouseRequest) =>
      api.codebookWarehouse.updateCodebookWarehouse(id!, request),
    onSuccess: () => {
      toaster.create({
        title: "Uspešno izmenjeno!",
        description: "Proizvod je uspešno izmenjen",
        type: "info",
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["codebookWarehouse"] });
    },
    onError: () => {
      toaster.create({
        title: "Greška!",
        description: "Proverite unete podatke",
        type: "error",
        duration: 5000,
      });
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutate({
      code: data.code,
      name: data.name,
      type: data.type[0],
      plateType: data.plateType[0],
      note: data.note,
      stoneSurfaceTreatment: data.stoneSurfaceTreatment,
    });
  });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="pt-[5.25rem] h-full xl:h-full p-[1rem] bg-[#ffffff] flex flex-col items-center">
      <div className="w-full flex flex-col sm:flex-row justify-between items-center px-[0.25rem]">
        <div className="flex flex-row gap-[1rem] items-center">
          <span className={"text-[24px] text-black font-semibold"}>
            ŠIFARNIK
          </span>
        </div>
        <div className="flex flex-row gap-[1rem] items-center">
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              disabled={false}
              onClick={onSubmit}
            >
              <IoCheckmark /> Izmeni
            </Button>
          </div>
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              onClick={() => resetFields()}
            >
              <FaTrashAlt /> Poništi
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full h-full flex flex-col gap-[1rem] mt-[1rem] items-center overflow-scroll">
        <div className="w-full flex flex-col items-center gap-[1rem] justify-between mt-[1rem] px-[0.25rem]">
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"code"}
            placeholder={"Unesi šifru materijala"}
            label={"Šifra materijala"}
          />
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"name"}
            placeholder={"Unesi naziv materijala"}
            label={"Naziv materijala"}
          />
          <ControlledDropdown
            name={"type"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Tip materijala"}
            placeholder={"Izaberi tip materijala"}
            options={MaterialSelectProps}
          />
          <ControlledDropdown
            name={"plateType"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Tip ploče"}
            placeholder={"Izaberi tip ploče"}
            options={PlateSelectProps}
          />
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"stoneSurfaceTreatment"}
            placeholder={"Unesi obradu površine kamena (opciono)"}
            label={"Obrada površine kamena"}
          />
          <ControlledTextArea
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"note"}
            placeholder={"Unesi napomenu (opciono)"}
            label={"Napomena"}
          />
          <div className={"h-[1rem]"}></div>
        </div>
      </div>
    </div>
  );
};

export const UpdateCodebookWarehousePage = () => {
  return (
    <ControlledForm schema={updateCodebookWarehouseSchema}>
      <UpdateCodebookWarehouseForm />
    </ControlledForm>
  );
};
